import * as React from 'react';

//PACKAGES
import { Link, useHistory } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

//UTILS
import { validationErrorsToString } from '../../utils';

//API
import apibridge from '../../apibridge';

//STORE
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// ICONS
import { ReactComponent as LogOutIcon } from '../../assets/svg/logout.svg';
import LoadingSpinner from '../../components/LoadingSpinner';

//COMPONENTS
import Button from '../../components/Button';

// TYPES
import { AccountUserResponse } from '../../api/models';

interface LandingDashboardTypes {}

const LandingDashboard: React.FC<LandingDashboardTypes> = ({}) => {
	const dispatch = useAppDispatch();
	const history = useHistory();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get the website URL and admin email from the store
	const websiteURL = useAppSelector((store) => store.lists.websiteUrl);
	const accountsEmail = useAppSelector((store) => store.lists.accountsEmail);

	// Check if the user has an incomplete organisation
	const [hasIncompleteProfile, setHasIncompleteProfile] = React.useState<boolean>(false);
	const [incompletProfileId, setIncompleteProfileId] = React.useState<string>('');

	// Check if has RequireAdminOrganisationIdentifier
	const [hasRequireAdminOrganisationIdentifier, setHasRequireAdminOrganisationIdentifier] =
		React.useState<boolean>(false);
	const [organisationIdentifier, setOrganisationIdentifier] = React.useState<string>('');

	//Check is the user has a pending request
	const [hasPendingRequest, setHasPendingRequest] = React.useState<boolean>(false);

	const getUserDetails = async () => {
		setIsLoading(true);
		const response = await apibridge.getUserDetails();

		if (response.isError) {
			setIsLoading(false);
			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);
			if (response?.result?.profiles && response?.result?.profiles.length > 0) {
				// If they land on landing dashboard and they have a profile, redirect them to dashboard
				history.push('/dashboard');
			} else {
				// Has pending request
				if (response?.result?.hasPendingRequests) {
					setHasPendingRequest(true);
				} else {
					// Has incomplete organisation
					if (response?.result?.incompleteOrganisationId) {
						setHasIncompleteProfile(true);
						setIncompleteProfileId(response.result.incompleteOrganisationId);
					} else {
						//Has RequireAdminOrganisationIdentifier
						if (response?.result?.requireAdminOrganisationIdentifier) {
							setHasRequireAdminOrganisationIdentifier(true);
							setOrganisationIdentifier(response.result.requireAdminOrganisationIdentifier);
						}
					}
				}
			}
		}
	};

	React.useEffect(() => {
		getUserDetails();
	}, []);

	const doLogout = () => {
		dispatch(UserSlice.actions.logOut());
	};

	const goToHomepage = () => {
		window.location.href = websiteURL || '';
	};

	const goToJoin = () => {
		history.push('/find-organisation');
	};

	const goToRegister = () => {
		history.push('/register-organisation');
	};

	const goToContinueJoin = () => {
		history.push(`/join-organisation?id=${organisationIdentifier}&type=staff`);
	};

	return (
		<div className="landing-dashboard-container">
			<SwitchTransition mode="out-in">
				<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
					{isLoading ? (
						<LoadingSpinner show={isLoading} />
					) : !!formErrorMessage ? (
						<div className="form-validation-error">{formErrorMessage}</div>
					) : hasPendingRequest ? (
						// Has pending request
						<>
							<h2>
								Thank you for requesting to join this organisation.
								<br /> Your request has been sent to the organisation and you will be notified via email when it has
								been reviewed.
							</h2>

							<section className="link-group">
								<Button className="btn btn-primary" onClick={() => goToHomepage()}>
									Return to homepage
								</Button>

								<Button className="btn btn-primary" onClick={() => goToJoin()}>
									Join another organisation
								</Button>

								<Button className="btn btn-primary" onClick={doLogout}>
									<LogOutIcon /> Logout
								</Button>
							</section>
						</>
					) : hasIncompleteProfile ? (
						// Has incomplete organisation
						<>
							<div className="payment-error">
								<h2>
									Payment for your organisation’s ChildAware account has not been completed. If you have made a payment
									please contact ChildAware at <a href={`mailto:${accountsEmail || ''}`}>accounts@childaware.com</a> for
									assistance. Otherwise, please make a{' '}
									<Link to={`/register-organisation-payment?id=${incompletProfileId}`}>payment</Link> to complete this
									process.
								</h2>
							</div>
						</>
					) : hasRequireAdminOrganisationIdentifier ? (
						// Has RequireAdminOrganisationIdentifier
						<>
							<h2>
								Thank you for joining ChildAware. You previously created an organisation on ChildAware but did not
								finalise joining it.
							</h2>

							<section className="link-group">
								<Button className="btn btn-primary" onClick={() => goToContinueJoin()}>
									Finalise joining organisation
								</Button>

								<Button className="btn btn-primary" onClick={() => goToJoin()}>
									Join another organisation
								</Button>

								<Button className="btn btn-primary" onClick={doLogout}>
									<LogOutIcon /> Logout
								</Button>
							</section>
						</>
					) : (
						// No pending request
						<>
							<h2>
								Thank you for joining ChildAware. Your account is not currently associated with organisations on
								ChildAware.
							</h2>

							<section className="link-group">
								<Button className="btn btn-primary" onClick={() => goToJoin()}>
									Join an organisation
								</Button>

								<Button className="btn btn-primary" onClick={() => goToRegister()}>
									Create an organisation
								</Button>

								<Button className="btn btn-primary" onClick={doLogout}>
									<LogOutIcon /> Logout
								</Button>
							</section>
						</>
					)}
				</CSSTransition>
			</SwitchTransition>
		</div>
	);
};

export default LandingDashboard;
